import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaInjector from '@/components/base/SchemaInjector';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { organizationSchema, websiteSchema, getWebPageSchema, getBreadcrumbSchema, getFAQSchema } from '@/mocks/schema';

const aboutFaqData = [
  {
    question: '传媒在线观看平台的定位是什么？',
    answer: '传媒在线观看是一个面向中文用户的综合性视频内容平台，致力于为用户提供高清直播、点播、回看全场景的视频观看体验。平台覆盖新闻资讯、综艺娱乐、纪录片、影视剧、体育赛事、教育文化等六大内容品类，无需下载安装，打开浏览器即可畅享海量视频资源。',
  },
  {
    question: '传媒在线观看有什么技术优势？',
    answer: '平台采用先进的流媒体传输技术和智能CDN分发网络，确保视频播放流畅不卡顿。支持自适应码率技术，根据用户网络状况自动调整画质，最高支持4K超高清播放。同时，基于人工智能的个性化推荐系统，能够根据用户的观看偏好精准推送内容。',
  },
  {
    question: '如何与传媒在线观看合作？',
    answer: '我们欢迎优质内容创作者和版权方与我们合作。无论您是独立创作者、媒体机构还是内容平台，都可以通过我们的联系方式提交合作意向。我们的内容团队将在收到申请后的3个工作日内与您取得联系，共同探讨合作方案。',
  },
];

const milestones = [
  { year: '2022', event: '传媒在线观看平台正式上线，推出新闻和综艺两大内容品类' },
  { year: '2023', event: '平台月活用户突破1000万，新增纪录片和影视剧场频道' },
  { year: '2024', event: '上线直播频道功能，支持200+电视频道实时直播观看' },
  { year: '2025', event: '月活用户突破3000万，引入4K超高清画质，推出AI智能推荐系统' },
  { year: '2026', event: '月活用户突破5000万，上线教育文化频道，成为中文视频平台领导者' },
];

const advantages = [
  {
    icon: 'ri-flashlight-line',
    title: '无需下载',
    desc: '纯网页端体验，打开浏览器即可观看，不占用设备存储空间',
  },
  {
    icon: 'ri-4k-line',
    title: '超高清画质',
    desc: '支持最高4K分辨率播放，自适应码率技术确保流畅体验',
  },
  {
    icon: 'ri-live-line',
    title: '直播+点播',
    desc: '实时直播与海量点播资源结合，全场景覆盖观看需求',
  },
  {
    icon: 'ri-smartphone-line',
    title: '多端适配',
    desc: '完美适配PC、平板、手机等多种设备，随时随地观看',
  },
  {
    icon: 'ri-ai-generate',
    title: '智能推荐',
    desc: 'AI驱动的个性化推荐，精准匹配您的观看偏好',
  },
  {
    icon: 'ri-shield-check-line',
    title: '正版授权',
    desc: '所有内容均获正版授权，平台合规运营，观看更放心',
  },
];

export default function AboutPage() {
  useScrollToTop();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const webPageSchema = getWebPageSchema(
    'https://www.chuanmeizaixian.com/about',
    '关于我们 - 传媒在线观看',
    '传媒在线观看品牌介绍、平台优势及联系方式。了解传媒在线观看的发展历程、核心优势和合作机会。',
  );
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: 'https://www.chuanmeizaixian.com/' },
    { name: '关于我们', url: 'https://www.chuanmeizaixian.com/about' },
  ]);
  const faqSchema = getFAQSchema(aboutFaqData);

  return (
    <>
      <SchemaInjector schema={[organizationSchema, websiteSchema, webPageSchema, breadcrumbSchema, faqSchema]} />

      <main>
        <Navbar />

        <div className="pt-20 bg-background-100">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: '关于我们' },
            ]}
          />
        </div>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground-900 mb-5">
              关于传媒在线观看
            </h1>
            <p className="text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              传媒在线观看致力于为广大中文用户提供最优质的在线视频观看体验。我们汇聚海量高清内容，打造一站式视频平台，让每一位用户都能轻松找到并享受喜爱的节目。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
              平台优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {advantages.map((adv, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-xl border border-background-200/70 p-6 transition-all duration-300 hover:border-primary-300"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className={`${adv.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">
                    {adv.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {adv.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-[23px] top-2 bottom-2 w-0.5 bg-primary-200 hidden sm:block"></div>
              <div className="space-y-8">
                {milestones.map((m, index) => (
                  <div key={index} className="flex gap-5 items-start">
                    <div className="hidden sm:flex w-12 h-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-bold text-sm z-10">
                      {m.year.slice(2)}
                    </div>
                    <div className="flex-1 bg-background-50 rounded-xl border border-background-200/70 p-5">
                      <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium mb-2 sm:hidden">
                        {m.year}
                      </span>
                      <p className="text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-center mb-10">
              如果您有任何疑问、合作意向或建议，欢迎通过以下方式与我们取得联系
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-600">contact@chuanmeizaixian.com</p>
              </div>
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">客服热线</h3>
                <p className="text-sm text-foreground-600">400-888-0000</p>
              </div>
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">公司地址</h3>
                <p className="text-sm text-foreground-600">北京市朝阳区传媒大厦</p>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl border border-background-200/70 p-8">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">
                发送消息
              </h3>
              <form
                id="contact-form"
                data-readdy-form="true"
                action="https://readdy.ai/api/form/d9q9c0snlsngrm4lltn0"
                method="POST"
                className="space-y-5 max-w-lg mx-auto"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const hpValue = (formData.get('phone_alt') as string || '').trim();
                  if (hpValue) {
                    alert('消息已发送，我们会尽快回复您！');
                    form.reset();
                    return;
                  }
                  try {
                    const response = await fetch('https://readdy.ai/api/form/d9q9c0snlsngrm4lltn0', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                    });
                    const responseText = await response.text();
                    if (response.ok) {
                      alert('消息已发送，我们会尽快回复您！');
                      form.reset();
                    } else {
                      alert('发送失败，请稍后重试或通过邮箱联系我们。');
                    }
                  } catch {
                    alert('网络错误，请稍后重试。');
                  }
                }}
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您想说的内容（500字以内）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送消息
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
              常见问题
            </h2>
            <div className="space-y-3">
              {aboutFaqData.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                  >
                    <h4 className="font-medium text-foreground-900 text-sm pr-4">
                      {faq.question}
                    </h4>
                    <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full transition-colors ${
                      openFaqIndex === index ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'
                    }`}>
                      <i className={`text-sm transition-transform duration-300 ${openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}